"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.area = exports.distance = void 0;
var _validates_1 = require("./_validates");
var constants_1 = require("./constants");
var errors_1 = require("./errors");
var utils_1 = require("./utils");
var _distance = function (p1, p2, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    // https://vldb.gsi.go.jp/sokuchi/surveycalc/surveycalc/algorithm/bl2st/bl2st.htm
    // https://www.tandfonline.com/doi/abs/10.1179/sre.1996.33.261.461
    (0, _validates_1.validPoint)(p1);
    (0, _validates_1.validPoint)(p2);
    var options = Object.assign({
        semimajorAxis: constants_1.SEMEMAJOR_AXIS_WGS84,
        flattening: constants_1.FLATTENING_WGS84,
        truncation: 1e-15,
        maxCount: 100,
    }, userOptions);
    (0, _validates_1.validNumber)(options.semimajorAxis);
    (0, _validates_1.validNumber)(options.flattening);
    (0, _validates_1.validNumber)(options.truncation);
    (0, _validates_1.validNumber)(options.maxCount);
    var lon1 = (0, utils_1._toRadians)(p1[0]);
    var lat1 = (0, utils_1._toRadians)(p1[1]);
    var lon2 = (0, utils_1._toRadians)(p2[0]);
    var lat2 = (0, utils_1._toRadians)(p2[1]);
    if ((0, utils_1._eq)(lon1, lon2) && (0, utils_1._eq)(lat1, lat2))
        return 0; // At this time, not converge calculation.
    var a = options.semimajorAxis;
    var f = 1 / options.flattening;
    var lonDiff = lon2 - lon1;
    var lonDiffWarp = lonDiff;
    if (lonDiff > Math.PI)
        lonDiffWarp -= 2 * Math.PI;
    else if (lonDiff < -Math.PI)
        lonDiffWarp += 2 * Math.PI;
    var L = Math.abs(lonDiffWarp);
    var L_d = Math.PI - L;
    var Sigma = lat1 + lat2;
    var u1 = lonDiffWarp >= 0
        ? Math.atan((1 - f) * Math.tan(lat1))
        : Math.atan((1 - f) * Math.tan(lat2));
    var u2 = lonDiffWarp >= 0
        ? Math.atan((1 - f) * Math.tan(lat2))
        : Math.atan((1 - f) * Math.tan(lat1));
    var Sigma_d = u1 + u2;
    var Delta_d = u2 - u1;
    var xi = Math.cos(Sigma_d / 2);
    var xi_d = Math.sin(Sigma_d / 2);
    var eta = Math.sin(Delta_d / 2);
    var eta_d = Math.cos(Delta_d / 2);
    var x = Math.sin(u1) * Math.sin(u2);
    var y = Math.cos(u1) * Math.cos(u2);
    var c = y * Math.cos(L) + x;
    var epsilon = (f * (2 - f)) / Math.pow((1 - f), 2);
    var theta, zone;
    if (c >= 0) {
        theta = L * (1 + f * y);
        zone = 1;
    }
    else if (c >= -Math.cos((0, utils_1._toRadians)(3) * Math.cos(u1))) {
        theta = L_d;
        zone = 2;
    }
    else {
        zone = 3;
        var R = f *
            Math.PI *
            Math.pow(Math.cos(u1), 2) *
            (1 -
                (f * (1 + f) * Math.pow(Math.sin(u1), 2)) / 4 +
                (3 / 16) * Math.pow(f, 2) * Math.pow(Math.sin(u1), 4));
        var d1 = L_d * Math.cos(u1) - R;
        var d2 = Math.abs(Sigma_d) + R;
        var q = L_d / (f * Math.PI);
        var f1 = (f * (1 + f / 2)) / 4;
        var gamma0 = q + f1 * q - f1 * Math.pow(q, 3);
        if ((0, utils_1._eq)(Sigma, 0)) {
            if (d1 > 0) {
                //b1
                theta = L_d;
            }
            else if ((0, utils_1._eq)(d1, 0)) {
                // b2
                var Gamma_1 = Math.pow(Math.sin(u1), 2);
                var n0_1 = (epsilon * Gamma_1) / Math.pow((Math.sqrt(1 + epsilon * Gamma_1) + 1), 2);
                var A_1 = (1 + n0_1) * (1 + (5 / 4) * Math.pow(n0_1, 2));
                return (1 - f) * a * A_1 * Math.PI;
            }
            else {
                // b3
                var cnt_1 = 0;
                var gamma0_1 = 0;
                var Gamma_2 = 0;
                while (cnt_1 < options.maxCount) {
                    Gamma_2 = 1 - Math.pow(gamma0_1, 2);
                    var D = f * (1 + f) / 4 - 3 / 16 * Math.pow(f, 2) * Gamma_2;
                    var gamma1 = q * (1 - D * Gamma_2);
                    if (Math.abs(gamma0_1 - gamma1) < options.truncation)
                        break;
                    gamma0_1 = gamma1;
                    cnt_1 += 1;
                }
                if (cnt_1 >= options.maxCount)
                    throw new errors_1.NotConvergeCalculationError();
                var n0_2 = (epsilon * Gamma_2) / Math.pow((Math.sqrt(1 + epsilon * Gamma_2) + 1), 2);
                var A_2 = (1 + n0_2) * (1 + (5 / 4) * Math.pow(n0_2, 2));
                return (1 - f) * a * A_2 * Math.PI;
            }
        }
        else {
            // a
            var A0 = Math.atan(d1 / d2);
            var B0 = Math.asin(R / Math.sqrt(Math.pow(d1, 2) + Math.pow(d2, 2)));
            var psi = A0 + B0;
            var j = gamma0 / Math.cos(u1);
            var k = ((1 + f1) * Math.abs(Sigma_d) * (1 - f * y)) / (f * Math.PI * y);
            var j1 = j / (1 + k * Math.sin(psi));
            var psi_d = Math.asin(j1);
            var psi_dd = Math.asin((j1 * Math.cos(u1)) / Math.cos(u2));
            theta =
                2 *
                    Math.atan((Math.tan((psi_d + psi_dd) / 2) * Math.sin(Math.abs(Sigma_d) / 2)) /
                        Math.cos(Delta_d / 2));
        }
    }
    var cnt = 0;
    var Gamma = 0, sigma = 0, zeta = 0, J = 0, K = 0;
    while (cnt < options.maxCount) {
        var g = zone === 1
            ? Math.sqrt(Math.pow(eta, 2) * Math.pow(Math.cos(theta / 2), 2) +
                Math.pow(xi, 2) * Math.pow(Math.sin(theta / 2), 2))
            : Math.sqrt(Math.pow(eta, 2) * Math.pow(Math.sin(theta / 2), 2) +
                Math.pow(xi, 2) * Math.pow(Math.cos(theta / 2), 2));
        var h = zone === 1
            ? Math.sqrt(Math.pow(eta_d, 2) * Math.pow(Math.cos(theta / 2), 2) +
                Math.pow(xi_d, 2) * Math.pow(Math.sin(theta / 2), 2))
            : Math.sqrt(Math.pow(eta_d, 2) * Math.pow(Math.sin(theta / 2), 2) +
                Math.pow(xi_d, 2) * Math.pow(Math.cos(theta / 2), 2));
        sigma = 2 * Math.atan(g / h);
        J = 2 * g * h;
        K = Math.pow(h, 2) - Math.pow(g, 2);
        var gamma = (y * Math.sin(theta)) / J;
        Gamma = 1 - Math.pow(gamma, 2);
        zeta = Gamma * K - 2 * x;
        var zeta_d = zeta + x;
        var D = (f * (1 + f)) / 4 - (3 / 16) * Math.pow(f, 2) * Gamma;
        var E = (1 - D * Gamma) *
            f *
            gamma *
            (sigma + D * J * (zeta + D * K * (2 * Math.pow(zeta, 2) - Math.pow(Gamma, 2))));
        var F = zone === 1 ? theta - L - E : theta - L_d + E;
        var G = f * Math.pow(gamma, 2) * (1 - 2 * D * Gamma) +
            ((f * zeta_d * sigma) / J) * (1 - D * Gamma + (f * Math.pow(gamma, 2)) / 2) +
            (Math.pow(f, 2) * zeta * zeta_d) / 4;
        theta -= F / (1 - G);
        if (Math.abs(F) < options.truncation)
            break;
        cnt += 1;
    }
    if (cnt >= options.maxCount)
        throw new errors_1.NotConvergeCalculationError();
    var n0 = (epsilon * Gamma) / Math.pow((Math.sqrt(1 + epsilon * Gamma) + 1), 2);
    var A = (1 + n0) * (1 + (5 / 4) * Math.pow(n0, 2));
    var B = (epsilon * (1 - (3 * Math.pow(n0, 2)) / 8)) /
        Math.pow((Math.sqrt(1 + epsilon * Gamma) + 1), 2);
    return ((1 - f) *
        a *
        A *
        (sigma -
            B *
                J *
                (zeta -
                    (B *
                        (K * (Math.pow(Gamma, 2) - 2 * Math.pow(zeta, 2)) -
                            (B * zeta * (1 - 4 * Math.pow(K, 2)) * (3 * Math.pow(Gamma, 2) - 4 * Math.pow(zeta, 2))) /
                                6)) /
                        4)));
};
var _linestringDistance = function (geometry, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    return _arrayDistance(geometry.coordinates, userOptions);
};
var _multiLinestringPolygonDistance = function (geometry, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    var ret = 0;
    for (var i = 0; i < geometry.coordinates.length; i++) {
        ret += _arrayDistance(geometry.coordinates[i], userOptions);
    }
    return ret;
};
var _multiPolygonDistance = function (geometry, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    var ret = 0;
    for (var i = 0; i < geometry.coordinates.length; i++) {
        ret += _multiLinestringPolygonDistance({
            type: "Polygon",
            coordinates: geometry.coordinates[i],
        }, userOptions);
    }
    return ret;
};
var _arrayDistance = function (points, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    var ret = 0;
    for (var i = 0; i < points.length - 1; i++) {
        ret += _distance(points[i], points[i + 1], userOptions);
    }
    return ret;
};
function distance(data, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    if (Array.isArray(data)) {
        return _arrayDistance(data, userOptions);
    }
    switch (data === null || data === void 0 ? void 0 : data.type) {
        case "Feature":
            return distance(data.geometry, userOptions);
        case "LineString":
            return _linestringDistance(data, userOptions);
        case "MultiLineString":
            return _multiLinestringPolygonDistance(data, userOptions);
        case "Polygon":
            return _multiLinestringPolygonDistance(data, userOptions);
        case "MultiPolygon":
            return _multiPolygonDistance(data, userOptions);
    }
    throw new errors_1.NotSupportMeasuringDistance();
}
exports.distance = distance;
var _latitudeBelt = function (latRad, e) {
    var v = e * Math.sin(latRad);
    return v / (1 - Math.pow(v, 2)) + Math.atanh(v);
};
var _authalicLatitude = function (latRad, e) {
    return Math.asin(_latitudeBelt(latRad, e) / _latitudeBelt(Math.PI / 2, e));
};
var _haversine = function (lonRad1, latRad1, lonRad2, latRad2) {
    return (2 *
        Math.asin(Math.sqrt(Math.pow(Math.sin((latRad1 - latRad2) / 2), 2) +
            Math.cos(latRad1) *
                Math.cos(latRad2) *
                Math.pow(Math.sin((lonRad1 - lonRad2) / 2), 2))));
};
var _area = function (linearRing, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    // https://maps.gsi.go.jp/help/pdf/calc_area.pdf
    (0, _validates_1.validLinearRing)(linearRing);
    var options = Object.assign({
        semimajorAxis: constants_1.SEMEMAJOR_AXIS_WGS84,
        flattening: constants_1.FLATTENING_WGS84,
    }, userOptions);
    (0, _validates_1.validNumber)(options.semimajorAxis);
    (0, _validates_1.validNumber)(options.flattening);
    var f = 1 / options.flattening;
    var e2 = f * (2 - f);
    var e = Math.sqrt(e2);
    var r2 = (_latitudeBelt(Math.PI / 2, e) / 2) *
        Math.pow(options.semimajorAxis, 2) *
        (1 / e - e);
    var area = 0;
    var lon0 = (0, utils_1._toRadians)(linearRing[0][0]);
    var lat0 = _authalicLatitude((0, utils_1._toRadians)(linearRing[0][1]), e);
    var lon1 = (0, utils_1._toRadians)(linearRing[1][0]);
    var lat1 = _authalicLatitude((0, utils_1._toRadians)(linearRing[1][1]), e);
    var l = linearRing.length - 2;
    for (var i = 1; i < l; i++) {
        var lon2 = (0, utils_1._toRadians)(linearRing[i + 1][0]);
        var lat2 = _authalicLatitude((0, utils_1._toRadians)(linearRing[i + 1][1]), e);
        var prime = Math.cos(lat0) *
            Math.cos(lon0) *
            Math.cos(lat1) *
            Math.sin(lon1) *
            Math.sin(lat2) +
            Math.cos(lat1) *
                Math.cos(lon1) *
                Math.cos(lat2) *
                Math.sin(lon2) *
                Math.sin(lat0) +
            Math.cos(lat2) *
                Math.cos(lon2) *
                Math.cos(lat0) *
                Math.sin(lon0) *
                Math.sin(lat1) -
            Math.cos(lat2) *
                Math.cos(lon2) *
                Math.cos(lat1) *
                Math.sin(lon1) *
                Math.sin(lat0) -
            Math.cos(lat1) *
                Math.cos(lon1) *
                Math.cos(lat0) *
                Math.sin(lon0) *
                Math.sin(lat2) -
            Math.cos(lat0) *
                Math.cos(lon0) *
                Math.cos(lat2) *
                Math.sin(lon2) *
                Math.sin(lat1) >
            0
            ? 1
            : -1;
        var d1 = _haversine(lon0, lat0, lon1, lat1);
        var d2 = _haversine(lon1, lat1, lon2, lat2);
        var d3 = _haversine(lon2, lat2, lon0, lat0);
        var s = (d1 + d2 + d3) / 2;
        area +=
            prime *
                4 *
                Math.atan(Math.sqrt(Math.abs(Math.tan(s / 2) *
                    Math.tan((s - d1) / 2) *
                    Math.tan((s - d2) / 2) *
                    Math.tan((s - d3) / 2))));
        lon1 = lon2;
        lat1 = lat2;
    }
    return Math.abs(r2 * area);
};
var _polygonArea = function (polygon, userOptions) {
    var ret = 0;
    if (Array.isArray(polygon === null || polygon === void 0 ? void 0 : polygon.coordinates)) {
        ret += _area(polygon.coordinates[0], userOptions);
        for (var i = 1; i < polygon.coordinates.length; i++) {
            ret -= _area(polygon.coordinates[i], userOptions);
        }
    }
    return ret;
};
var _multiPolygonArea = function (polygon, userOptions) {
    var ret = 0;
    if (Array.isArray(polygon === null || polygon === void 0 ? void 0 : polygon.coordinates)) {
        for (var i = 0; i < polygon.coordinates.length; i++)
            ret += _polygonArea({
                type: "Polygon",
                coordinates: polygon.coordinates[i],
            }, userOptions);
    }
    return ret;
};
function area(data, userOptions) {
    if (userOptions === void 0) { userOptions = {}; }
    if (Array.isArray(data)) {
        return _area(data, userOptions);
    }
    switch (data === null || data === void 0 ? void 0 : data.type) {
        case "Feature":
            return area(data.geometry, userOptions);
        case "Polygon":
            return _polygonArea(data, userOptions);
        case "MultiPolygon":
            return _multiPolygonArea(data, userOptions);
    }
    throw new errors_1.NotSupportMeasuringArea();
}
exports.area = area;
